import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DataTable from '../../components/ui/DataTable';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput';
import DateTimePicker from '../../components/ui/DateTimePicker';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  getExpenseBudgets, createExpenseBudget, deleteExpenseBudget,
} from '../../services/expenseDashboardService';
import { getDepartments } from '../../services/departmentService';
import { getLocations } from '../../services/locationService';
import { formatDate } from '../../utils/Utils';

const Dialog = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 33, 33, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
`;

const DialogCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  width: 440px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const DialogTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const emptyForm = () => ({ scope: 'company', departmentId: null, locationId: null, validFrom: null, monthlyAmount: '' });

export default function ExpenseBudgetsPage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageIndex: 1, pageSize: 20 });
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 20 });
  const [refresh, setRefresh] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    (async () => {
      setFetching(true);
      try {
        const res = await getExpenseBudgets(queryParams);
        const payload = res.data?.data;
        setData(payload?.data ?? []);
        setPagination({
          totalRowCount: payload?.totalRowCount ?? 0,
          pageIndex: payload?.pageIndex ?? 1,
          pageSize: payload?.pageSize ?? 20,
        });
      } catch { /* interceptor */ } finally {
        setFetching(false);
      }
    })();
  }, [queryParams, refresh]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getDepartments({ pageSize: 200, orderBy: 'name' });
        setDepartments(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
      try {
        const res = await getLocations({ pageSize: 200, orderBy: 'name' });
        setLocations(res.data?.data?.data ?? []);
      } catch { /* interceptor */ }
    })();
  }, []);

  const scopeLabels = useMemo(() => ({
    company: t('expenseBudget.scopeCompany'),
    department: t('expenseBudget.scopeDepartment'),
    location: t('expenseBudget.scopeLocation'),
  }), [t]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'scope',
      label: t('expenseBudget.scope'),
      options: { filter: false, sort: false, customBodyRender: (v) => scopeLabels[v] || v },
    },
    {
      name: 'target',
      label: t('expenseBudget.target'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, meta) => {
          const row = data[meta.rowIndex];
          return row?.departmentName || row?.locationName || t('expenseBudget.wholeCompany');
        },
      },
    },
    {
      name: 'validFrom',
      label: t('expenseBudget.validFrom'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => new Date(v).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }),
      },
    },
    {
      name: 'monthlyAmount',
      label: t('expenseBudget.monthlyAmount'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => `${Number(v ?? 0).toLocaleString('tr-TR')} ₺`,
      },
    },
    {
      name: 'createDate',
      label: t('expenseBudget.createDate'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, i18n.language) },
    },
  ], [t, i18n.language, data, scopeLabels]);

  const isFormValid = form.scope
    && form.validFrom
    && parseFloat(form.monthlyAmount) >= 0 && form.monthlyAmount !== ''
    && (form.scope !== 'department' || form.departmentId)
    && (form.scope !== 'location' || form.locationId);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await createExpenseBudget({
        scope: form.scope,
        departmentId: form.scope === 'department' ? form.departmentId : null,
        locationId: form.scope === 'location' ? form.locationId : null,
        validFrom: form.validFrom,
        monthlyAmount: parseFloat(form.monthlyAmount) || 0,
      });
      setDialogOpen(false);
      setForm(emptyForm());
      setRefresh((p) => p + 1);
    } catch { /* interceptor */ } finally {
      setSaving(false);
    }
  }, [form]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteExpenseBudget(deleteConfirm.id);
      setRefresh((p) => p + 1);
    } catch { /* interceptor */ } finally {
      setDeleteConfirm({ isOpen: false, id: null });
    }
  }, [deleteConfirm.id]);

  return (
    <div>
      <DataTable
        title={t('expenseBudget.title')}
        canCreateNewRecord
        newButtonLabel={t('expenseBudget.new')}
        onNewButtonClick={() => { setForm(emptyForm()); setDialogOpen(true); }}
        data={data}
        columns={columns}
        fetching={fetching}
        pagination={pagination}
        onTableChange={(action, tableState) => {
          if (action === 'changePage' || action === 'changeRowsPerPage') {
            setQueryParams({ pageNumber: tableState.page + 1, pageSize: tableState.rowsPerPage });
          }
        }}
        options={{
          filter: false,
          search: false,
          onRowsDelete: (rowsDeleted) => {
            const id = data[rowsDeleted.data[0]?.dataIndex]?.id;
            if (id) setDeleteConfirm({ isOpen: true, id });
            return false;
          },
        }}
      />

      {dialogOpen && (
        <Dialog onClick={(e) => { if (e.target === e.currentTarget) setDialogOpen(false); }}>
          <DialogCard>
            <DialogTitle>{t('expenseBudget.new')}</DialogTitle>
            <GenericSelect
              label={t('expenseBudget.scope')}
              options={[
                { id: 'company', name: scopeLabels.company },
                { id: 'department', name: scopeLabels.department },
                { id: 'location', name: scopeLabels.location },
              ]}
              value={form.scope}
              onChange={(v) => setForm((p) => ({ ...p, scope: v || 'company', departmentId: null, locationId: null }))}
              placeholder={t('expenseBudget.scope')}
            />
            {form.scope === 'department' && (
              <GenericSelect
                label={t('expenseBudget.department')}
                options={departments}
                value={form.departmentId}
                onChange={(v) => setForm((p) => ({ ...p, departmentId: v }))}
                placeholder={t('expenseBudget.department')}
                required
              />
            )}
            {form.scope === 'location' && (
              <GenericSelect
                label={t('expenseBudget.location')}
                options={locations}
                value={form.locationId}
                onChange={(v) => setForm((p) => ({ ...p, locationId: v }))}
                placeholder={t('expenseBudget.location')}
                required
              />
            )}
            <DateTimePicker
              label={t('expenseBudget.validFrom')}
              value={form.validFrom}
              onChange={(val) => setForm((p) => ({ ...p, validFrom: val }))}
              mode="date"
            />
            <TextInput
              label={t('expenseBudget.monthlyAmount')}
              type="number"
              min="0"
              value={form.monthlyAmount}
              onChange={(e) => setForm((p) => ({ ...p, monthlyAmount: e.target.value }))}
              required
            />
            <DialogActions>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={!isFormValid || saving}>{t('common.save')}</Button>
            </DialogActions>
          </DialogCard>
        </Dialog>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('expenseBudget.deleteTitle')}
        message={t('expenseBudget.deleteMessage')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null })}
      />
    </div>
  );
}
